import { Element, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";
import { Actions } from "src/types";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Button from "@/components/editor/primitives/button/button-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import {
  DEFAULT_BUTTON_TEXT,
  DEFAULT_HEADING_TEXT,
  DEFAULT_SUBHEADING_TEXT,
} from "@/components/editor/shared/constants";
import { whiteLogo } from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import useEvent from "@/hooks/use-event";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";
import humanizeDateRange from "@/utils/dates/humanizeDateRange";

import {
  ButtonWrapper,
  Container,
  Content,
  Image,
  ImageWrapper,
  Logo,
  LogoWrapper,
} from "./header-option-3-component-styles";

type HeaderOption3Props = {
  backgroundImage?: string;
  backgroundImageAlt?: string;
  backgroundPosition?: ImagePosition;
  isLogoShowing?: boolean;
  logo?: string;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => {
  return <HeaderSettings optimalImageSize="1360x700px" />;
};

export default function HeaderOption3(props: HeaderOption3Props) {
  const {
    id: nodeId,
    connectors: { connect },
  } = useNode();
  const { event } = useEvent();

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "cta-text",
    "subheading",
  ]);

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockMaxWidthWrapper maxWidth={rem(1440)}>
        <Container>
          <ImageWrapper>
            <Image
              imageFit={props.imageFit}
              $position={props.backgroundPosition || "center center"}
              src={props.backgroundImage}
              alt={props.backgroundImageAlt || ""}
            />
          </ImageWrapper>
          <Content>
            {props.isLogoShowing && (
              <LogoWrapper>
                <Logo
                  draggable="false"
                  src={props.logo || whiteLogo}
                  alt="Header Logo"
                  style={{
                    width: formatLogoSize(props.logoSize),
                    height: "auto",
                  }}
                  isLogoResized={!isNaN(props.logoSize)}
                />
              </LogoWrapper>
            )}
            {isRichTextCompatible ? (
              <>
                <Element
                  id="heading"
                  is={RichText}
                  text={`<h2 style="text-align:center;">${
                    event.name || DEFAULT_HEADING_TEXT
                  }</h2>`}
                />
                <Element
                  id="subheading"
                  is={RichText}
                  text={`<p style="text-align:center;">${DEFAULT_SUBHEADING_TEXT}</p>`}
                />
                <Element
                  id="cta-text"
                  is={RichText}
                  text={`<h5 style="text-align:center;">${humanizeDateRange(
                    event.time_start,
                    event.time_end,
                    event.standard_timezone
                  ).join(" - ")}</h5>`}
                />
              </>
            ) : (
              <>
                <Element
                  id="heading"
                  is={Text}
                  element="h1"
                  text={event.name || DEFAULT_HEADING_TEXT}
                  pattern="headingOne"
                />

                <Element
                  id="subheading"
                  is={Text}
                  text={DEFAULT_SUBHEADING_TEXT}
                  pattern="paragraph"
                />

                <Element
                  id="cta-text"
                  is={Text}
                  element="h4"
                  text={humanizeDateRange(
                    event.time_start,
                    event.time_end,
                    event.standard_timezone
                  ).join(" - ")}
                  pattern="headingFour"
                />
              </>
            )}
            <ButtonWrapper>
              <Element
                id="cta-button"
                is={Button}
                text={DEFAULT_BUTTON_TEXT}
                action={{
                  kind: Actions.TicketSelector,
                }}
              />
            </ButtonWrapper>
          </Content>
        </Container>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

HeaderOption3.craft = {
  custom: {
    displayName: "Header option #3",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
